import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useMemo,
} from "react";
import { Config, ModelConfig } from "../../model_gen/config";
import { ConfigService } from "../../services/ConfigService";
import { usePermissions } from "../../PermissionsProvider";
import {
  ConfigurationPanelProps,
  ConfigurationPanelRef,
} from "../../types/IApplication";
import { InferenceLocations } from "../../types/IConfig";
import ModelSelect from "./ModelSelect";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Slider } from "../ui/slider";
import { Tooltip } from "../ui/tooltip";
import { Separator } from "../ui/separator";
import { ChevronDownIcon, ChevronUpIcon, InfoIcon, SettingsIcon, BrainIcon, SlidersIcon } from "lucide-react";
import { cn } from "../../lib/utils";

const ConfigurationPanel = forwardRef<
  ConfigurationPanelRef,
  ConfigurationPanelProps
>((props, ref) => {
  const { apiKey } = usePermissions();
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState<boolean>(false);
  const [models, setModels] = useState<ModelConfig>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [unsavedConfig, setUnsavedConfig] = useState<Partial<Config> | null>(
    null
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [justSaved, setJustSaved] = useState<boolean>(false);
  const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Create a merged config that combines props.config with unsavedConfig
  const currentConfig = useMemo(() => {
    if (!props.config) return null;
    if (!unsavedConfig) return props.config;
    return {
      ...props.config,
      ...unsavedConfig,
    };
  }, [props.config, unsavedConfig]);

  const getModels = () => {
    setIsLoading(true);
    ConfigService.getModels(
      props?.intentGroupId ?? -1,
      setIsLoading,
      apiKey
    ).subscribe(
      (models: ModelConfig) => {
        // Sort the models in alphabetical order
        models.client_models.sort();
        models.server_models.sort();
        setModels(models);
      },
      (error: any) => {
        setIsLoading(false);
        console.error(error);
      }
    );
  };

  const updateConfig = (new_config: Partial<Config>, save: boolean = false) => {
    if (save) {
      setIsSaving(true);
      setJustSaved(false);
      
      // Clear any pending timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      props.updateConfig(
        new_config,
        (loading: boolean) => {
          setIsSaving(loading);
          if (!loading) {
            setUnsavedConfig(null);
            
            // Delay "Saved" indicator to allow error handling
            saveTimeoutRef.current = setTimeout(() => {
              setJustSaved(true);
            }, 200);
          }
        },
        true
      );
    } else {
      setUnsavedConfig((prev) => ({
        ...prev,
        ...new_config,
      }));
      // Clear "Saved" state on new changes
      if (justSaved) {
        setJustSaved(false);
      }
    }
  };

  const handleSaveChanges = () => {
    if (unsavedConfig) {
      updateConfig(unsavedConfig, true);
    }
  };

  // Expose save state to parent component
  useEffect(() => {
    if (props.onSaveStateChange) {
      props.onSaveStateChange(!!unsavedConfig, isSaving);
    }
  }, [unsavedConfig, isSaving, props.onSaveStateChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    getModels();
  }, []);

  useImperativeHandle(ref, () => ({
    closeToolTipEvent() {
      // Tooltip functionality can be implemented later if needed
    },
    openToolTipEvent() {
      // Tooltip functionality can be implemented later if needed
    },
    saveChanges: handleSaveChanges,
    hasUnsavedChanges: !!unsavedConfig,
    isSaving: isSaving,
  }));

  const hasO3Model = (config?: Config) => {
    const primaryHasO3 = config?.model?.includes("o3");
    const secondaryHasO3 =
      config?.fallback_config?.model?.includes("o3") ||
      config?.fallback_model?.includes("o3");
    return primaryHasO3 || secondaryHasO3;
  };

  // Loading state
  if (isLoading || !currentConfig) {
    return (
      <div className="space-y-6">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="border-border">
            <CardHeader>
              <div className="h-5 bg-muted rounded w-40 animate-pulse" />
            </CardHeader>
            <CardContent className="space-y-4">
              {[...Array(3)].map((_, j) => (
                <div key={j} className="space-y-2">
                  <div className="h-4 bg-muted rounded w-32 animate-pulse" />
                  <div className="h-10 bg-muted rounded animate-pulse" />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* General Configuration */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <SettingsIcon className="w-5 h-5 text-maitai-green" />
            General Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Inference Location */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-gc-med text-foreground">Inference Location</Label>
                <Tooltip content="Choose where the model inference will take place">
                  <InfoIcon className="w-4 h-4 text-muted-foreground" />
                </Tooltip>
              </div>
              <RadioGroup
                value={currentConfig?.inference_location?.toString()}
                onValueChange={(value) => {
                  if (!props.readOnly) {
                    const numericValue = parseInt(value) as InferenceLocations;
                    updateConfig({
                      inference_location: numericValue,
                      model:
                        numericValue === InferenceLocations.CLIENT
                          ? models?.default_client_model
                          : currentConfig?.model,
                      context_retrieval_enabled:
                        numericValue === InferenceLocations.SERVER
                          ? currentConfig?.context_retrieval_enabled
                          : false,
                    });
                  }
                }}
                disabled={props.readOnly || isSaving}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem 
                    value={InferenceLocations.CLIENT.toString()} 
                    id="client" 
                    disabled={props.disableClientInference}
                  />
                  <Label 
                    htmlFor="client" 
                    className={cn(
                      "text-sm font-gc-reg",
                      props.disableClientInference && "opacity-50"
                    )}
                  >
                    Client
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value={InferenceLocations.SERVER.toString()} id="server" />
                  <Label htmlFor="server" className="text-sm font-gc-reg">Server</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Indexing Enabled */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-gc-med text-foreground">Indexing Enabled</Label>
                <Tooltip content="When enabled, request metadata will be extracted and indexed for analysis. Disabling at the application or intent group level will disable indexing for all associated intents.">
                  <InfoIcon className="w-4 h-4 text-muted-foreground" />
                </Tooltip>
              </div>
              <Switch
                checked={currentConfig?.extract_request_metadata}
                disabled={props.readOnly || isSaving}
                onCheckedChange={(checked) => {
                  if (!props.readOnly) {
                    updateConfig({ extract_request_metadata: checked });
                  }
                }}
              />
            </div>

            {/* Reasoning Effort (O3 models only) */}
            {hasO3Model(currentConfig) && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-gc-med text-foreground">Reasoning Effort</Label>
                  <Tooltip content="Controls the model's reasoning depth and thoroughness">
                    <InfoIcon className="w-4 h-4 text-muted-foreground" />
                  </Tooltip>
                </div>
                <Select
                  value={currentConfig?.reasoning_effort || "medium"}
                  onValueChange={(value) => {
                    if (!props.readOnly && !isSaving) {
                      updateConfig({ reasoning_effort: value });
                    }
                  }}
                  disabled={props.readOnly || isSaving}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <Separator className="border-border" />

          {/* Toggle Settings */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-gc-med text-foreground">Evaluations</Label>
                <Tooltip content="Enable or disable model evaluations">
                  <InfoIcon className="w-4 h-4 text-muted-foreground" />
                </Tooltip>
              </div>
              <Switch
                checked={currentConfig?.evaluation_enabled}
                disabled={props.readOnly || isSaving}
                onCheckedChange={(checked) => {
                  if (!props.readOnly) {
                    updateConfig({
                      evaluation_enabled: checked,
                      apply_corrections: !checked
                        ? false
                        : currentConfig?.apply_corrections,
                    });
                  }
                }}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-gc-med text-foreground">Apply Corrections</Label>
                <Tooltip content="Automatically apply corrections based on evaluations">
                  <InfoIcon className="w-4 h-4 text-muted-foreground" />
                </Tooltip>
              </div>
              <Switch
                checked={currentConfig?.apply_corrections}
                disabled={props.readOnly || isSaving || !currentConfig?.evaluation_enabled}
                onCheckedChange={(checked) => {
                  if (!props.readOnly) {
                    updateConfig({
                      apply_corrections: checked,
                      inference_location: checked
                        ? InferenceLocations.SERVER
                        : currentConfig?.inference_location,
                      evaluation_enabled: checked
                        ? true
                        : currentConfig?.evaluation_enabled,
                    });
                  }
                }}
              />
            </div>

          </div>

          {/* Safe Mode - Show only when apply_corrections is enabled */}
          {currentConfig?.apply_corrections && (
            <>
              <Separator className="border-border" />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-gc-med text-foreground">Safe Mode</Label>
                  <Tooltip content="Safe mode prioritizes accuracy over latency">
                    <InfoIcon className="w-4 h-4 text-muted-foreground" />
                  </Tooltip>
                </div>
                <Switch
                  checked={currentConfig?.safe_mode}
                  disabled={props.readOnly || isSaving}
                  onCheckedChange={(checked) => {
                    if (!props.readOnly) {
                      updateConfig({ safe_mode: checked });
                    }
                  }}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Model Configuration */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <BrainIcon className="w-5 h-5 text-maitai-green" />
            Model Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Primary and Secondary Models */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Primary Model */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-gc-med text-foreground">Primary Model</Label>
                <Tooltip content="The primary AI model to be used for inference">
                  <InfoIcon className="w-4 h-4 text-muted-foreground" />
                </Tooltip>
              </div>
              <ModelSelect
                value={currentConfig?.model}
                onChange={(value: string) => {
                  if (!props.readOnly && !isSaving) {
                    updateConfig({ model: value });
                  }
                }}
                disabled={props.readOnly || isSaving}
                label=""
                placeholder="Select a model"
                intentGroupId={props?.intentGroupId}
                modelType={
                  currentConfig?.inference_location === InferenceLocations.CLIENT
                    ? "client"
                    : "server"
                }
              />
            </div>

            {/* Secondary Model */}
            {props.allowSecondaryModel !== false && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-gc-med text-foreground">Secondary Model</Label>
                  <Tooltip content="The model to fallback to if the primary model is not available or has degraded performance">
                    <InfoIcon className="w-4 h-4 text-muted-foreground" />
                  </Tooltip>
                </div>
                <ModelSelect
                  value={
                    currentConfig?.fallback_config?.model ??
                    currentConfig?.fallback_model
                  }
                  onChange={(value: string) => {
                    if (!props.readOnly && !isSaving) {
                      if (value === "") {
                        updateConfig({
                          fallback_config: {
                            model: "",
                            strategy: "reactive",
                            timeout: 0,
                          },
                        });
                      } else {
                        updateConfig({
                          fallback_config: {
                            ...currentConfig?.fallback_config,
                            model: value,
                          },
                        });
                      }
                    }
                  }}
                  disabled={props.readOnly || isSaving}
                  label=""
                  placeholder="Select a secondary model"
                  intentGroupId={props?.intentGroupId}
                  modelType={
                    currentConfig?.inference_location === InferenceLocations.CLIENT
                      ? "client"
                      : "server"
                  }
                />
              </div>
            )}
          </div>

          {/* Fallback Configuration - Show only when secondary model is selected */}
          {(currentConfig?.fallback_config?.model || currentConfig?.fallback_model) && (
            <>
              <Separator className="border-border" />
              <div className="grid md:grid-cols-2 gap-6">
                {/* Fallback Strategy */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-gc-med text-foreground">Fallback Strategy</Label>
                    <Tooltip content="Strategy to use when falling back to secondary model">
                      <InfoIcon className="w-4 h-4 text-muted-foreground" />
                    </Tooltip>
                  </div>
                  <Select
                    value={currentConfig?.fallback_config?.strategy ?? "reactive"}
                    onValueChange={(value) => {
                      updateConfig({
                        fallback_config: {
                          ...currentConfig?.fallback_config,
                          strategy: value,
                          timeout:
                            value === "timeout"
                              ? currentConfig?.fallback_config?.timeout
                              : undefined,
                        },
                      });
                    }}
                    disabled={props.readOnly || isSaving}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reactive">Reactive</SelectItem>
                      <SelectItem value="timeout">Timeout</SelectItem>
                      <SelectItem value="first_response">First Response</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Fallback Timeout - Show only for timeout strategy */}
                {currentConfig?.fallback_config?.strategy === "timeout" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-gc-med text-foreground">Timeout (seconds)</Label>
                      <Tooltip content="Timeout in seconds before falling back to secondary model">
                        <InfoIcon className="w-4 h-4 text-muted-foreground" />
                      </Tooltip>
                    </div>
                    <Input
                      type="number"
                      min={0}
                      step={0.1}
                      value={currentConfig?.fallback_config?.timeout ?? ""}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        updateConfig({
                          fallback_config: {
                            ...currentConfig?.fallback_config,
                            timeout: isNaN(value) ? undefined : value,
                          },
                        });
                      }}
                      disabled={props.readOnly || isSaving}
                      placeholder="Timeout in seconds"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          <Separator className="border-border" />

          {/* Temperature */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-gc-med text-foreground">Temperature</Label>
                <Tooltip content="Controls randomness in the model's output (0 = deterministic, 2 = very random)">
                  <InfoIcon className="w-4 h-4 text-muted-foreground" />
                </Tooltip>
              </div>
              <span className="text-sm font-gc-med text-maitai-green">
                {currentConfig?.temperature?.toFixed(2) ?? "1.00"}
              </span>
            </div>
            <Slider
              value={[currentConfig?.temperature ?? 1]}
              onValueChange={([value]) => {
                if (!props.readOnly && !isSaving) {
                  updateConfig({ temperature: value });
                }
              }}
              disabled={props.readOnly || isSaving}
              min={0}
              max={2}
              step={0.01}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0</span>
              <span>1</span>
              <span>2</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Settings */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
                         <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
               <SlidersIcon className="w-5 h-5 text-maitai-green" />
               Advanced Settings
             </CardTitle>
            <Button
              variant="ghost"
              onClick={() => setIsAdvancedExpanded(!isAdvancedExpanded)}
              disabled={isSaving}
              className="gap-2 text-sm"
            >
              {isAdvancedExpanded ? (
                <>
                  <ChevronUpIcon className="w-4 h-4" />
                  Hide
                </>
              ) : (
                <>
                  <ChevronDownIcon className="w-4 h-4" />
                  Show
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        
        {isAdvancedExpanded && (
          <CardContent className="space-y-6">
            {/* First Row: Stop Sequence and Log Probs */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-gc-med text-foreground">Stop Sequence</Label>
                  <Tooltip content="Sequences where the API will stop generating further tokens">
                    <InfoIcon className="w-4 h-4 text-muted-foreground" />
                  </Tooltip>
                </div>
                <Input
                  placeholder='["", ".", "..."]'
                  disabled={props.readOnly || isSaving}
                  onBlur={(e) => {
                    updateConfig({ stop: e.target.value });
                  }}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-gc-med text-foreground">Log Probs</Label>
                  <Tooltip content="Include the log probabilities of the output tokens">
                    <InfoIcon className="w-4 h-4 text-muted-foreground" />
                  </Tooltip>
                </div>
                <Switch
                  checked={currentConfig?.logprobs}
                  disabled={props.readOnly || isSaving}
                  onCheckedChange={(checked) => {
                    if (!props.readOnly && !isSaving) {
                      updateConfig({
                        logprobs: checked,
                        apply_corrections: !checked
                          ? false
                          : currentConfig?.apply_corrections,
                      });
                    }
                  }}
                />
              </div>
            </div>

            <Separator className="border-border" />

            {/* Second Row: N and Max Tokens */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-gc-med text-foreground">N</Label>
                  <Tooltip content="How many completions to generate for each prompt">
                    <InfoIcon className="w-4 h-4 text-muted-foreground" />
                  </Tooltip>
                </div>
                <Input
                  type="number"
                  min={0}
                  value={currentConfig?.n ?? ""}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!props.readOnly && !isSaving && !isNaN(value)) {
                      updateConfig({ n: value });
                    }
                  }}
                  disabled={props.readOnly || isSaving}
                  placeholder="N"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-gc-med text-foreground">Max Tokens</Label>
                  <Tooltip content="The maximum number of tokens to generate in the completion">
                    <InfoIcon className="w-4 h-4 text-muted-foreground" />
                  </Tooltip>
                </div>
                <Input
                  type="number"
                  min={0}
                  value={currentConfig?.max_tokens ?? ""}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!props.readOnly && !isSaving && !isNaN(value)) {
                      updateConfig({ max_tokens: value });
                    }
                  }}
                  disabled={props.readOnly || isSaving}
                  placeholder="Tokens"
                />
              </div>
            </div>

            <Separator className="border-border" />

            {/* Third Row: Penalties */}
            <div className="space-y-6">
              {/* Presence Penalty */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-gc-med text-foreground">Presence Penalty</Label>
                    <Tooltip content="Increases the model's likelihood to talk about new topics">
                      <InfoIcon className="w-4 h-4 text-muted-foreground" />
                    </Tooltip>
                  </div>
                  <span className="text-sm font-gc-med text-maitai-green">
                    {currentConfig?.presence_penalty?.toFixed(2) ?? "0.00"}
                  </span>
                </div>
                <Slider
                  value={[currentConfig?.presence_penalty ?? 0]}
                  onValueChange={([value]) => {
                    if (!props.readOnly && !isSaving) {
                      updateConfig({ presence_penalty: value });
                    }
                  }}
                  disabled={props.readOnly || isSaving}
                  min={-2}
                  max={2}
                  step={0.01}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>-2</span>
                  <span>0</span>
                  <span>2</span>
                </div>
              </div>

              {/* Frequency Penalty */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-gc-med text-foreground">Frequency Penalty</Label>
                    <Tooltip content="Decreases the model's likelihood to repeat the same line verbatim">
                      <InfoIcon className="w-4 h-4 text-muted-foreground" />
                    </Tooltip>
                  </div>
                  <span className="text-sm font-gc-med text-maitai-green">
                    {currentConfig?.frequency_penalty?.toFixed(2) ?? "0.00"}
                  </span>
                </div>
                <Slider
                  value={[currentConfig?.frequency_penalty ?? 0]}
                  onValueChange={([value]) => {
                    if (!props.readOnly && !isSaving) {
                      updateConfig({ frequency_penalty: value });
                    }
                  }}
                  disabled={props.readOnly || isSaving}
                  min={-2}
                  max={2}
                  step={0.01}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>-2</span>
                  <span>0</span>
                  <span>2</span>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Save/Reset Actions - Only show when not in modal mode */}
      {!props.isModalOpen && (
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <div>
            {unsavedConfig && (
              <span className="text-sm text-amber-600 dark:text-amber-400">
                You have unsaved changes
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => {
                if (props.resetConfig) {
                  props.resetConfig();
                  setUnsavedConfig(null);
                }
              }}
              disabled={isSaving || !unsavedConfig || props.readOnly}
            >
              Reset to Default
            </Button>
            <Button
              onClick={handleSaveChanges}
              disabled={isSaving || !unsavedConfig || props.readOnly}
            >
              {isSaving ? "Saving..." : justSaved ? "Saved" : "Save Changes"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

export default ConfigurationPanel;
